import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './navbar.css';
import {
  getNavbarBorder,
  LANGUAGES,
  menuItemsNew,
  useProfileMenuItemPart,
  useRightMenuPart,
} from '@/components';
import { PermissionType } from '@/type';
import { Menu, MenuItem } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import Select from '@mui/material/Select';
import MenuItems from './MenuItems';
import {
  hasDashboardViewPermission,
  hasPermission,
  invalidateDashboardQueryKey,
  invalidateLoadExtractedStopsToDashboardQuery,
} from '@/hooks';
import { APP_ROUTES } from '@/util';
import { getRuntimeConfig, HcLinearAuth } from '@hclinear/hc-linear-react';
import { useNavigate } from '@tanstack/react-router';
import { languageLocalStorageService } from '@service/localStorage/languageLocalStorageService';

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasDashboardView = hasDashboardViewPermission();

  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMenuClick = (event, menuId) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveDropdown(menuId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveDropdown(null);
  };

  const navigateToAuthAdmin = () => {
    window.location.href = getRuntimeConfig().AUTH_FRONTEND_URL;
  };

  const hasPerm = (permissions?: PermissionType | PermissionType[]): boolean => {
    if (!permissions) return true;

    const storedPermissions = HcLinearAuth.getRoles();
    if (!storedPermissions) return false;

    if (typeof permissions === 'string') {
      return storedPermissions.includes(permissions);
    }

    return permissions.some((permission) => storedPermissions.includes(permission));
  };

  const onLogoutClick = async () => {
    await HcLinearAuth.signOut();
  };

  const navigateToProfile = () => navigate({ to: APP_ROUTES.USER.PROFILE });

  const onTimerExpire = async () => {
    await Promise.all([
      invalidateDashboardQueryKey(),
      invalidateLoadExtractedStopsToDashboardQuery(),
    ]);
  };

  const rightMenuItems = useRightMenuPart(
    handleClick,
    onLogoutClick,
    open,
    onTimerExpire,
  );
  const profileMenuItems = useProfileMenuItemPart(
    t,
    HcLinearAuth?.getAuthenticatedUser()?.username,
    handleClose,
    navigateToAuthAdmin,
    navigateToProfile,
    hasPermission([PermissionType.PERM_MENU_AUTH_SERVER_VIEW]),
  );

  const navigateToDashboard = () => navigate({ to: APP_ROUTES.DASHBOARD });

  // TODO: modify to set the localization to i18n and don't reload the page
  const handleChange = (event) => {
    languageLocalStorageService.setLanguage(event.target.value);
    window.location.reload();
  };

  return (
    <>
      {HcLinearAuth.isAuthenticated() && (
        <nav className='desktop-nav' style={getNavbarBorder()}>
          <div className='nav-container'>
            <div className='nav-brand'>
              <img
                style={{ cursor: 'pointer' }}
                src='/assets/png/header-logo.svg'
                alt='Logo'
                className='logo'
                onClick={navigateToDashboard}
              />
            </div>
            <div className='menus'>
              {menuItemsNew(!hasDashboardView)
                .filter((item) => hasPerm(item.roles))
                .map((item) => (
                  <MenuItems
                    item={item}
                    open={activeDropdown}
                    handleMenuClick={handleMenuClick}
                    handleMenuClose={handleMenuClose}
                    navigate={navigate}
                    hasPermission={hasPerm}
                    menuAnchorEl={menuAnchorEl}
                  />
                ))}
            </div>
          </div>
          <div className='right-menu'>
            {rightMenuItems.map((menuItem) => (
              <React.Fragment key={menuItem.id}>{menuItem.icon}</React.Fragment>
            ))}

            <Menu
              id='profile-menu'
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'profile-button',
              }}
              PaperProps={{
                style: {
                  marginTop: '20px',
                  backgroundColor: 'var(--background-color-dark)',
                  color: '#fff',
                },
              }}
            >
              {profileMenuItems.map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={item.onClick}
                  sx={{
                    width: '250px',
                    height: '50px',
                    '&:first-of-type': {
                      height: '60px',
                    },
                    borderBottom: '1px solid #26586B',
                    '& .MuiTypography-root': {
                      fontSize: '14px',
                      color: '#fff',
                    },
                  }}
                >
                  {item.icon}
                  {item.text}
                </MenuItem>
              ))}
              <Select
                value={languageLocalStorageService.getLanguage()}
                onChange={handleChange}
                sx={selectStyle}
                renderValue={(selected) => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LanguageIcon style={{ marginRight: '10px', color: '#fff' }} />
                    {LANGUAGES.find((lang) => lang.code === selected)?.label ||
                      t('common.select-language')}
                  </div>
                )}
              >
                {LANGUAGES.map(({ code, label }) => (
                  <MenuItem
                    key={code}
                    value={code}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'var(--hover-background-color)',
                      },
                    }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Menu>
          </div>
        </nav>
      )}
    </>
  );
};

export default Navbar;
export const selectStyle = {
  height: '50px',
  width: '100%',
  color: '#fff',
  '.MuiSelect-select': {
    paddingLeft: '10px',
  },
  '&:hover': {},
  borderBottom: '1px solid #26586B',
  border: '0px !important',
  outline: '0px',
  backgroundColor: 'var(--background-color-dark)',
  '.MuiOutlinedInput-root': {
    border: 'none',
    '&.Mui-focused': {
      border: 'none',
    },
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    paddingLeft: '10px',
  },
};
